import { useRef, useMemo, useEffect } from "react";
import PropTypes from "prop-types";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";

// Defaults builders
import { ComplexCommissionTier, GrowthBonusTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// Tiers Layouts
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";

// Constants
const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const minimumGrossInput = -100;
const maximumGrossInput = 10000;

const minimumVolumeInput = -100;
const maximumVolumeInput = 10000;

const checkBoxesHaveError = (args) => {
	const { make, options, tiers, componentErrors } = args;
	const errors = [];

	const columns = tiers?.[0]?.bonuses?.length;
	const rows = tiers?.length;

	if (!options?.isFinance && !options?.isRetail && !options?.isFleet && !options?.isWholesale) {
		errors.push("Please select at least one checkbox.");
	}
	if (options?.isFinance && rows < 2) {
		errors.push("F&I needs at least two tiers to assess growth.");
	}

	componentErrors.current[make] = rows <= 1 && columns <= 1;

	if (Object.values(componentErrors.current).every((item) => item)) {
		errors.push("Please add at least a two tiers configuration");
	}

	return errors;
};

const updateValueColumn = (args) => {
	const { currentTiers, column, updatedValue, area } = args;

	currentTiers.forEach((tier) => {
		tier.bonuses?.forEach((bonus, key) => {
			if (area in bonus && key === column) {
				bonus[area]["value"] = updatedValue;
				if (tier.bonuses?.[key + 1]) {
					tier.bonuses[key + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
				}
			}
		});
	});
};

const updateValueRow = (args) => {
	const { currentTiers, row, column, updatedValue, area } = args;

	if (area !== "bonus") {
		currentTiers[row][area]["value"] = updatedValue;
		if (currentTiers?.[row + 1] && area === "to") {
			currentTiers[row + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
		}
	} else {
		currentTiers[row]["bonuses"][column][area]["value"] = updatedValue;
	}
};

const mergeErrorsBonusesValidation = (results = new Map(), section = []) => {
	if (!results?.size) {
		return [];
	}

	const rows = results?.get("ROWS") || [];

	if (section.length === 1 && section[0].length === 1) {
		return [...rows];
	}

	const firstValue = section?.[0]?.[0]?.["bonus"]?.value;

	for (const row of section) {
		if (row.some((cell) => cell?.bonus?.value !== firstValue)) {
			return [...rows];
		}
	}

	return rows.map((row) => {
		return row.map(() => {
			return {
				bonus: true,
			};
		});
	});
};

export default function GrowthBonus(props) {
	const {
		make = "",
		tiers = [],
		readOnly = false,
		disableAddTier = false,
		disableAddColumn = false,
		options = {},
		checkBoxesMapErrors = {},
		componentErrors = {},
		setCheckBoxesMapErrors = () => {},
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();

	const localErrorsMessages = getErrorsMessages();

	const headerErrors = errors?.get("volumeGrowth");
	const rowsErrors = errors?.get("grossGrowth");
	const columnsErrors = errors?.get("bonuses");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const onTiersUpdate = (updatedTiers) => {
		const validToSave = isValid && !checkBoxesErrors?.length && Object.values(componentErrors.current).some((item) => !item);
		onActionValidation?.(make, validToSave);
		if (validToSave && wasUpdated.current) {
			onActionUpdate?.(make, updatedTiers);
		}
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	const checkBoxesErrors = checkBoxesHaveError({
		make,
		options,
		componentErrors,
		tiers: innerTiers,
	});

	// Methods
	const handleAddColumn = () => {
		const currentTiers = [...innerTiers];
		const bonusInput = currentTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = currentTiers?.[0]?.["bonuses"]?.length ? currentTiers[0]["bonuses"].length - 1 : 0;

		currentTiers.forEach((tier) => {
			tier.bonuses.push(GrowthBonusTier(0, maximumVolumeInput, { bonus: 0, input: bonusInput }));
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		wasUpdated.current = false;
		setInnerTiers(currentTiers);
	};

	const handleDeleteColumn = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			currentTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumVolumeInput;
			});
		}
		const updatedTiers = checkIsOnlySingleValue(currentTiers);
		update(updatedTiers);
	};

	const handleAddTier = () => {
		const currentTiers = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const lastInput = currentTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const previousBonusesLength = currentTiers?.[lastIndex]?.["bonuses"]?.length;

		const bonuses = [];
		for (let i = 0; i < previousBonusesLength; i++) {
			const previousFrom = currentTiers?.[0]?.["bonuses"]?.[i]?.["from"]?.["value"] || 0;
			const previousTo = currentTiers?.[0]?.["bonuses"]?.[i]?.["to"]?.["value"] || 0;
			bonuses[i] = GrowthBonusTier(previousFrom, previousTo, { bonus: 0, input: lastInput });
		}

		currentTiers.push(
			ComplexCommissionTier([(minimumGrossInput + 0.01).toFixed(2), "Percent"], [maximumGrossInput, "Percent"], {
				identifier: "bonuses",
				commissions: bonuses,
			})
		);

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = minimumGrossInput;
		}

		onActionValidation?.(make, false);
		wasUpdated.current = false;

		setInnerTiers(currentTiers);
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = tiers?.length ? currentTiers.length - 1 : 1;
		currentTiers[lastIndex].to.value = maximumGrossInput;
		const updatedTiers = checkIsOnlySingleValue(currentTiers);
		update(updatedTiers);
	};

	const checkIsOnlySingleValue = (currentTiers) => {
		if (currentTiers?.length === 1 && currentTiers[0]?.["bonuses"]?.length === 1) {
			currentTiers[0]["bonuses"][0]["bonus"]["value"] = 0;
			componentErrors.current[make] = true;
		}
		return currentTiers;
	};

	const handleUpdateInput = (...params) => {
		const [, , location, area, input] = params;
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(currentTiers);
	};

	const handleUpdateValue = (...params) => {
		const [row, column, location, area, value] = params;
		const currentTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updateValueColumn({ currentTiers, column, updatedValue, area });
				break;
			case "row":
				updateValueRow({ currentTiers, row, column, updatedValue, area });
				break;
			default:
				break;
		}

		update(currentTiers);
	};

	const update = (updatedTiers = []) => {
		wasUpdated.current = true;
		setInnerTiers(updatedTiers);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			setCheckBoxesMapErrors({
				...checkBoxesMapErrors,
				[make]: !checkBoxesErrors?.length,
			});

			validate([
				{
					definition: {
						useKey: "grossGrowth",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: ">=",
									deltas: { Percent: 0.01 }, // Add delta on upper value
									distinct: 1,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "volumeGrowth",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<", // If from < to is good,
								},
								{
									type: "STEP",
									on: "value",
									compare: "===",
									deltas: { Percent: 0.01 }, // Add delta on upper value
									distinct: 0,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Volume invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=", // If current <= next is good,
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=", // If upper > lower is good,
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsBonusesValidation,
							},
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
			]);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [innerTiers, validate]);

	useEffect(() => {
		setInnerTiers(clone(tiers));
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
	}, [tiers, setInnerTiers]);

	return (
		<Grid container item mt={2} xs={12}>
			<VolumeGrowthColumns
				readOnly={readOnly}
				innerTiers={innerTiers}
				disableAddColumn={disableAddColumn}
				handleDeleteColumn={handleDeleteColumn}
				handleAddColumn={handleAddColumn}
				headerErrors={headerErrors}
				handleUpdateValue={handleUpdateValue}
			/>

			<GrossGrowthTiers
				readOnly={readOnly}
				innerTiers={innerTiers}
				options={options}
				disableAddTier={disableAddTier}
				handleDeleteTier={handleDeleteTier}
				handleAddTier={handleAddTier}
				rowsErrors={rowsErrors}
				columnsErrors={columnsErrors}
				handleUpdateValue={handleUpdateValue}
				handleUpdateInput={handleUpdateInput}
				localErrorsMessages={localErrorsMessages}
				checkBoxesErrors={checkBoxesErrors}
			/>
		</Grid>
	);
}
GrowthBonus.propTypes = {
	make: PropTypes.any,
	tiers: PropTypes.array,
	readOnly: PropTypes.bool,
	disableAddTier: PropTypes.bool,
	disableAddColumn: PropTypes.bool,
	options: PropTypes.object,
	checkBoxesMapErrors: PropTypes.object,
	componentErrors: PropTypes.object,
	setCheckBoxesMapErrors: PropTypes.func,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};

const VolumeGrowthColumns = (props) => {
	const {
		readOnly = false,
		innerTiers = [],
		disableAddColumn = false,
		headerErrors = [],
		handleAddColumn = () => {},
		handleDeleteColumn = () => {},
		handleUpdateValue = () => {},
	} = props;

	return (
		<Grid item xs={12}>
			<TierColumns
				titleLabel={"Volume Growth"}
				titleContainerProps={{
					justifyContent: "flex-start",
					alignItems: "end",
					sx: { maxWidth: "241px", width: "241px", marginRight: "3px" },
				}}
				titleProps={{
					className: "tiered-title tiered-title-adjustment",
					sx: null,
				}}
				disabledDelete={readOnly || innerTiers?.[0]?.bonuses?.length <= minimumColumns}
				disabledAdd={disableAddColumn || innerTiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
				onClickDelete={() => handleDeleteColumn()}
				onClickAdd={() => handleAddColumn()}
			>
				<Stack direction={"row"} position={"relative"}>
					{innerTiers?.[0]?.bonuses?.map((tier, key) => {
						const indexKey = `volume_growth_bonus_${key}`;
						const fromDisabled = Boolean(!key);
						const toDisabled = Boolean(innerTiers?.[0]?.bonuses.length === key + 1);

						return (
							<TierColumnsItem key={indexKey} index={key} title={"Bonus"}>
								<MatrixColumn
									index={key}
									item={tier}
									errors={headerErrors}
									fromDisabled={fromDisabled}
									toDisabled={toDisabled}
									readOnly={readOnly}
									onActionUpdate={handleUpdateValue}
								/>
							</TierColumnsItem>
						);
					})}
				</Stack>
			</TierColumns>
		</Grid>
	);
};
VolumeGrowthColumns.propTypes = {
	readOnly: PropTypes.bool,
	innerTiers: PropTypes.array,
	disableAddColumn: PropTypes.bool,
	headerErrors: PropTypes.array,
	handleAddColumn: PropTypes.func,
	handleDeleteColumn: PropTypes.func,
	handleUpdateValue: PropTypes.func,
};

const GrossGrowthTiers = (props) => {
	const {
		readOnly = false,
		innerTiers = [],
		options = {},
		disableAddTier = false,
		rowsErrors = [],
		columnsErrors = [],
		localErrorsMessages = [],
		checkBoxesErrors = [],
		handleAddTier = () => {},
		handleDeleteTier = () => {},
		handleUpdateInput = () => {},
		handleUpdateValue = () => {},
	} = props;

	return (
		<Grid item xs={12} mt={1} minHeight={"135px"}>
			<TierRows
				showTitle
				title={"Gross Growth"}
				sxTitlePropsContainer={{
					top: 130,
					left: -10,
				}}
				centerTitleAfterRow={3}
				deleteButtonText={"Remove Tier"}
				addButtonText={"Add Tier"}
				deleteButtonDisabled={readOnly || innerTiers?.length <= 1 || (options?.isFinance && innerTiers?.length <= 2)}
				addButtonDisabled={disableAddTier || innerTiers?.length >= maximumTiers || readOnly}
				spaces={{
					mainSection: 235,
					columns: 195,
					totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
					totalRows: innerTiers?.length || 1,
					separation: 5.5,
				}}
				rowsAreaProps={{
					ml: "35px",
				}}
				onClickDelete={() => handleDeleteTier()}
				onClickAdd={() => handleAddTier()}
			>
				<TransitionGroup>
					<Collapse>
						<Stack direction="column" rowGap={2}>
							{innerTiers?.map((tier, key) => {
								const indexKey = `volume_growth_row_${key}`;
								const fromDisabled = Boolean(!key);
								const toDisabled = Boolean(innerTiers?.length === key + 1);
								const bonusesLength = innerTiers?.[0]?.bonuses?.length || 0;
								const reduction = bonusesLength * 10 + 45 + bonusesLength;

								return (
									<Grid key={indexKey} container item>
										<MatrixRow
											index={key}
											item={tier}
											fromDisabled={fromDisabled}
											toDisabled={toDisabled}
											spaces={{
												mainSection: 235,
												columns: 195,
												totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
												totalRows: innerTiers?.length || 1,
												separation: 5.5,
												reduction,
											}}
											rowsErrors={rowsErrors || []}
											columnsErrors={columnsErrors || []}
											readOnly={readOnly}
											spacing={"10.5px"}
											sxMainContent={{ minWidth: "235.5px", marginRight: "-13px" }}
											sxCells={{ minWidth: "184.42px" }}
											onActionUpdate={handleUpdateValue}
											onChangeType={handleUpdateInput}
										/>
									</Grid>
								);
							})}
						</Stack>
					</Collapse>
				</TransitionGroup>

				{Boolean(localErrorsMessages?.length || checkBoxesErrors?.length) && (
					<Grid container direction="column" rowSpacing={0.2} mt={1} mb={1} alignItems="flex-start">
						{localErrorsMessages.map((message, key) => {
							const indexKey = `local_error_${key}`;
							return <TierAlertError key={indexKey} text={message} />;
						})}
						{checkBoxesErrors.map((message, key) => {
							const indexKey = `checkbox_error_${key}`;
							return <TierAlertError key={indexKey} text={message} />;
						})}
					</Grid>
				)}
			</TierRows>
		</Grid>
	);
};
GrossGrowthTiers.propTypes = {
	readOnly: PropTypes.bool,
	innerTiers: PropTypes.array,
	options: PropTypes.object,
	disableAddTier: PropTypes.bool,
	rowsErrors: PropTypes.array,
	columnsErrors: PropTypes.array,
	localErrorsMessages: PropTypes.array,
	checkBoxesErrors: PropTypes.array,
	handleAddTier: PropTypes.func,
	handleDeleteTier: PropTypes.func,
	handleUpdateInput: PropTypes.func,
	handleUpdateValue: PropTypes.func,
};
